import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import { useOptimalMediaUrl } from "@hooks/useOptimalMediaUrl";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import { useState } from "react";
import { Document, Page } from "react-pdf";

export interface PDFRendererItem {
  cloudfrontUrl: string;
  thumbnailUrl?: string;
  mediaId: string;
}

export interface PDFRendererProps {
  item: PDFRendererItem;
  pdfWidth?: number;
  mode?: string;
}

function PDFRenderer({
  item,
  pdfWidth = 816,
  mode = "FlyerPreview",
}: PDFRendererProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfError, setPdfError] = useState(false);

  // Get the optimal URL based on priority logic
  // React Compiler automatically optimizes this hook call
  const { optimalUrl, isLoading } = useOptimalMediaUrl(
    item.cloudfrontUrl,
    item.thumbnailUrl,
    item.mediaId,
  );

  // React Compiler automatically memoizes these handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(false);
  };

  const onDocumentLoadError = () => {
    setPdfError(true);
  };

  // Fallback to image if PDF fails to load
  if (pdfError) {
    return (
      <SurveyPDFPage mode={mode}>
        <img
          src={optimalUrl}
          alt="PDF failed to load"
          className="block mx-auto max-w-full max-h-full object-contain"
        />
      </SurveyPDFPage>
    );
  }

  // Show loading while determining optimal URL
  if (isLoading) {
    return (
      <SurveyPDFPage mode={mode}>
        <div className="flex justify-center items-center h-full w-full">
          <EmeraldProgressSpinner
            title="Loading media..."
            size="medium"
            isWhite={false}
            determinate={false}
          />
        </div>
      </SurveyPDFPage>
    );
  }

  return (
    <Document
      file={optimalUrl}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadError={onDocumentLoadError}
      className="flex flex-col gap-y-4"
    >
      {Array.from({ length: numPages }, (_, index) => (
        <SurveyPDFPage key={`media_page_${index + 1}`} mode={mode}>
          <Page
            pageNumber={index + 1}
            width={pdfWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </SurveyPDFPage>
      ))}
    </Document>
  );
}

export default PDFRenderer;
